import { computed, nextTick, onScopeDispose, ref, unref, watch } from 'vue';
import type { Ref, WatchSource } from 'vue';
import { useDebounceFn, useEventListener } from '@vueuse/core';
import { SettingsSectionTracker } from '@/domain/reservations/settings-navigation';

export interface ISettingsSectionSpyOptions {
  /** The element that scrolls (not the window). */
  container: Ref<HTMLElement | null>;
  /**
   * The sections in page order. A ref rebuilds the tracking when the list
   * changes (the Menú page's categories), keeping the active section when it
   * still exists.
   */
  sections: readonly string[] | Ref<readonly string[]>;
  elementFor: (section: string) => HTMLElement | null;
  /** Height covered by sticky chrome at the top of the container. */
  topOffset: () => number;
  /** True once the sections are rendered. */
  ready: Ref<boolean>;
  /** Reconnects when this changes (e.g. the sticky head's height, which sets the band). */
  reconnectOn?: WatchSource<unknown>;
}

/** Longest time a chosen section stays highlighted while the page travels to it. */
const CHOICE_LOCK_MS = 1500;
/** Quiet time after the last scroll event that ends that travel. */
const SCROLL_SETTLE_MS = 200;

/**
 * Keeps the settings navigation in sync with the section the reader is on.
 * A section counts as "on screen" while it crosses the band between the
 * sticky navigation and 45 % of the container height. The highlight rules
 * live in `SettingsSectionTracker`; this composable only feeds it DOM events.
 */
export function useSettingsSectionSpy(options: ISettingsSectionSpyOptions) {
  const sections = computed(() => unref(options.sections));
  let tracker = new SettingsSectionTracker(sections.value);
  const active = ref(tracker.active);
  const observed = new Map<Element, string>();
  let observer: IntersectionObserver | null = null;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;

  function atEnd(): boolean {
    const element = options.container.value;
    return (
      !!element &&
      element.scrollHeight > element.clientHeight + 1 &&
      element.scrollTop + element.clientHeight >= element.scrollHeight - 2
    );
  }
  function clearSettle() {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = null;
  }
  function settleAfter(ms: number) {
    clearSettle();
    settleTimer = setTimeout(() => {
      settleTimer = null;
      active.value = tracker.settle(atEnd());
    }, ms);
  }
  function update() {
    active.value = tracker.update(atEnd());
  }
  function scrolled() {
    if (settleTimer) settleAfter(SCROLL_SETTLE_MS);
    update();
  }
  function disconnect() {
    clearSettle();
    observer?.disconnect();
    observer = null;
    observed.clear();
    tracker.reset();
  }
  function connect() {
    disconnect();
    const root = options.container.value;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    // One extra pixel so a section ending exactly at the band never counts.
    const top = Math.round(options.topOffset()) + 1;
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const section = observed.get(entry.target);
          if (section) tracker.setVisible(section, entry.isIntersecting);
        }
        update();
      },
      { root, rootMargin: `-${top}px 0px -55% 0px`, threshold: 0 }
    );
    for (const section of sections.value) {
      const element = options.elementFor(section);
      if (!element) continue;
      observed.set(element, section);
      observer.observe(element);
    }
  }
  /** Marks a section chosen from the navigation before scrolling to it. */
  function go(section: string) {
    tracker.choose(section, CHOICE_LOCK_MS);
    active.value = tracker.active;
    settleAfter(CHOICE_LOCK_MS);
  }

  watch(
    options.ready,
    (ready) => {
      if (ready) void nextTick(connect);
      else disconnect();
    },
    { immediate: true }
  );

  // A new section list: new tracker, same highlight when it still exists,
  // observers on the new elements once they render.
  watch(
    sections,
    (next, previous) => {
      if (
        previous &&
        next.length === previous.length &&
        next.every((section, index) => section === previous[index])
      )
        return;
      const current = active.value;
      tracker = new SettingsSectionTracker(next);
      if (next.includes(current)) tracker.active = current;
      active.value = tracker.active;
      if (options.ready.value) void nextTick(connect);
    },
    { flush: 'post' }
  );

  if (options.reconnectOn)
    watch(options.reconnectOn, () => {
      if (options.ready.value) void nextTick(connect);
    });
  useEventListener(options.container, 'scroll', scrolled, { passive: true });
  useEventListener(
    window,
    'resize',
    useDebounceFn(() => {
      if (options.ready.value) connect();
    }, 200)
  );
  onScopeDispose(disconnect);

  return { active, go };
}
